/**
 * Designed empty / loading / error states (§18). Consistent, calm, and never a
 * bare "Loading…": the queue uses skeleton rows that match the table so the
 * layout doesn't jump.
 */
import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon.js";
import { Button } from "./Button.js";

export function EmptyState({
  icon = "queue",
  title,
  hint,
  children,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div className="state state--empty" role="status">
      <div className="state__icon">
        <Icon name={icon} size={22} />
      </div>
      <p className="state__title">{title}</p>
      {hint && <p className="state__hint">{hint}</p>}
      {children}
    </div>
  );
}

export function ErrorState({
  title,
  hint,
  onRetry,
}: {
  title: string;
  hint?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state state--error" role="alert">
      <div className="state__icon state__icon--error">
        <Icon name="alert" size={22} />
      </div>
      <p className="state__title">{title}</p>
      {hint && <p className="state__hint">{hint}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

/** A single shimmer block; width/height accept any CSS length. */
export function Skeleton({ width = "100%", height = 12 }: { width?: number | string; height?: number | string }) {
  return <span className="skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** Table-shaped skeleton so the queue doesn't reflow when data arrives. */
export function SkeletonRows({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="skeleton-rows" aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <div className="skeleton-row" key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton key={c} width={c === 0 ? "60%" : c === cols - 1 ? "40%" : "80%"} />
          ))}
        </div>
      ))}
    </div>
  );
}
